// Batches (CONTROL V4 "Batches", handoff §7k): the shapes the API returns and
// the design's own wording and tones. Every figure is derived by the API from
// the member cycles' effective values (routes/batches.js); this screen
// filters, formats and explains, it never re-adds a batch weight.

import { dayShort, type Cycle } from '../productActivity/model'
import { itemExport } from '../productActivity/itemName'
import type { CellValue } from './xlsx'

export type BatchState = 'active' | 'field' | 'receipted'
export type StatusKey = 'all' | BatchState | 'excluded'

export interface Batch {
  id: string
  batch_code: string
  bin_code: string | null
  site_id: string | null
  site_name: string | null
  work_date: string | null
  product: string | null
  picking_job_id: string | null
  picking_job_name: string | null
  blocks: string[]
  items: number
  live: number
  state: BatchState
  excluded: boolean
  gross_kg: number | null
  credited_kg: number
  weighed_kg: number | null
  in_bin_kg: number
  qc: number
  dates: string[]
  jobs: number
  after_complete: number
  over: boolean
  over_by: number | null
  exceptions: string[]
  last_at: string | null
  last_local: string | null
  started_local: string | null
  started_by: string | null
  completed_at: string | null
  completed_local: string | null
  completed_by: string | null
  completed_source: string | null
  receipted_at: string | null
  receipted_local: string | null
  receipted_by: string | null
  receipted_source: string | null
  weighed_local: string | null
  weighed_by: string | null
  weighed_source: string | null
  weighing_id: string | null
  pick_temp_c: number | null
  packhouse_temp_c: number | null
}

export interface Settings {
  max_bin_kg: number | null
  overweight_warnings: boolean
  updated_at?: string | null
  updated_by?: string | null
}

export interface ListResponse {
  batches: Batch[]
  settings: Settings
}

export interface HistoryEntry {
  id: string
  type: string
  source: string
  who: string | null
  what: string
  why: string | null
  at: string
  at_local: string | null
}

export interface BinBrief {
  id: string
  batch_code: string
  product: string | null
  first_date: string | null
  in_bin_kg: number
}

export type Member = Cycle & { set_by_bin: boolean }

// A Crew stack weighing (§7k.2c): the scale reading and every bin on it, in
// scan order. estimate_kg is the field estimate the latest split used;
// linked is false once a bin's weight has been set in Control.
export interface WeighingBin {
  batch_id: string
  batch_code: string
  bin_code: string | null
  tare_kg: number
  estimate_kg: number
  weighed_kg: number | null
  linked: boolean
}
export interface Weighing {
  id: string
  source: string
  operator: string
  device_label: string | null
  captured_local: string | null
  gross_kg: number
  tare_total_kg: number
  net_kg: number
  // Where the empty weight came from: 'device' is the one recorded with the weighing (D7.7); CONTROL never edits it.
  tare_source?: string
  has_photo: boolean
  bins: WeighingBin[]
}

export interface DetailResponse {
  batch: Batch
  members: Member[]
  history: HistoryEntry[]
  bin_fills: { previous: BinBrief | null; next: BinBrief | null; count: number }
  picking_jobs: { id: string; name: string }[]
  settings: Settings
  weighing?: Weighing | null
}

// V4 STATE and statusOf: the pill and the line under the tiles.
export const STATE: Record<BatchState | 'excluded', { label: string; bg: string; fg: string; line: string }> = {
  active: { label: 'Active', bg: '#f2ece1', fg: '#5c5449', line: 'Field station is still scanning buckets into this batch.' },
  field: {
    label: 'Completed in field',
    bg: '#fbeecd',
    fg: '#7a5410',
    line: 'Field collection is finished. The bin has not been receipted at the packhouse.',
  },
  receipted: { label: 'Receipted at packhouse', bg: '#e8efd6', fg: '#3f4a2c', line: 'The physical bin QR was scanned in at the packhouse.' },
  excluded: {
    label: 'Excluded',
    bg: '#f2ece1',
    fg: '#6b6153',
    line: 'Every cycle in this batch is excluded, so it contributes nothing to production.',
  },
}
export const statusOf = (b: Batch) => STATE[b.excluded ? 'excluded' : b.state]

// V4's receipted line says the bin QR was scanned. A receipt marked here
// (the correction for a bin that arrived but was never scanned) must not
// claim a scan that never happened.
export function statusLine(b: Batch): string {
  if (!b.excluded && b.state === 'receipted' && b.receipted_source === 'manual') {
    return 'Marked receipted in CONTROL. The bin QR was never scanned in at the packhouse.'
  }
  return statusOf(b).line
}

export const STATUS_CHIPS: { key: StatusKey; label: string; count: (b: Batch) => boolean }[] = [
  { key: 'all', label: 'All', count: (b) => !b.excluded },
  { key: 'active', label: 'Active', count: (b) => !b.excluded && b.state === 'active' },
  { key: 'field', label: 'Completed in field', count: (b) => !b.excluded && b.state === 'field' },
  { key: 'receipted', label: 'Receipted', count: (b) => !b.excluded && b.state === 'receipted' },
  { key: 'excluded', label: 'Excluded', count: (b) => b.excluded },
]

// V4 bxMatches: Excluded shows only excluded batches; every other status hides
// them. The date period is applied by the API.
export function matches(b: Batch, status: StatusKey, query: string): boolean {
  if (status === 'excluded' ? !b.excluded : b.excluded) return false
  if (status !== 'all' && status !== 'excluded' && b.state !== status) return false
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [b.batch_code, b.bin_code, b.product, b.picking_job_name, b.site_name].join(' ').toLowerCase().includes(q)
}

const one = (n: number) => n.toFixed(1)

// The warning chips on a row, in the order V4 prints them.
export function warningChips(b: Batch): { label: string; bg: string; fg: string }[] {
  const out: { label: string; bg: string; fg: string }[] = []
  if (b.over && b.over_by !== null) out.push({ label: 'Overweight +' + one(b.over_by) + ' kg', bg: '#fbeecd', fg: '#7a5410' })
  if (b.exceptions.includes('receipted_never_completed')) out.push({ label: 'Receipted, never completed', bg: '#f8ded0', fg: '#8c491a' })
  if (b.qc > 0) out.push({ label: b.qc + ' with QC', bg: '#e9eddb', fg: '#4a5734' })
  if (b.dates.length > 1) out.push({ label: b.dates.length + ' days', bg: '#f2ece1', fg: '#5c5449' })
  return out
}

// "Mon 28 14:05" from the API's site-local 'YYYY-MM-DD HH:MI'.
export function localLabel(s: string | null | undefined): string {
  if (!s) return '—'
  const [d, t] = s.split(' ')
  return dayShort(d) + (t ? ' ' + t : '')
}

// The row's weight: what is physically in the bin, credited beneath when the
// two differ (V4 bxRows kg/kgSub).
export function rowWeight(b: Batch): { kg: string; sub: string } {
  const sub = Math.abs(b.in_bin_kg - b.credited_kg) >= 0.05 ? one(b.credited_kg) + ' credited' : ''
  return { kg: one(b.in_bin_kg) + ' kg', sub }
}

// The table's foot row: items and weight over the batches in view (the search
// and status chip applied), the weight as each row shows it.
export function totalRow(rows: Batch[]): { items: number; kg: string; sub: string } {
  const sum = (f: (b: Batch) => number) => Math.round(rows.reduce((a, b) => a + f(b), 0) * 10) / 10
  const inBin = sum((b) => b.in_bin_kg)
  const credited = sum((b) => b.credited_kg)
  return {
    items: rows.reduce((a, b) => a + b.items, 0),
    kg: one(inBin) + ' kg',
    sub: Math.abs(inBin - credited) >= 0.05 ? one(credited) + ' credited' : '',
  }
}

// V4 bxTotal over the batches in view.
export function totalLine(rows: Batch[]): string {
  const gross = Math.round(rows.reduce((a, b) => a + b.in_bin_kg, 0) * 10) / 10
  const credited = Math.round(rows.reduce((a, b) => a + b.credited_kg, 0) * 10) / 10
  return Math.abs(gross - credited) < 0.05 ? one(gross) + ' kg' : one(gross) + ' kg in the bins · ' + one(credited) + ' kg credited'
}

export function spanLine(dates: string[]): string {
  if (dates.length > 1) return dates.length + ' days · ' + dayShort(dates[0]) + ' to ' + dayShort(dates[dates.length - 1])
  return dates[0] ? dayShort(dates[0]) : '—'
}

export const tempLine = (t: number | null) => (t === null ? '—' : t + ' °C')

// The weight tile (V4 bxD kgLabel/kg/kgSub). A weighed bin leads with the
// scale figure, since the override is meaningless if the typed number never
// appears.
export function weightTile(b: Batch): { label: string; value: string; sub: string } {
  if (b.weighed_kg !== null) {
    const by = b.weighed_by ? ' by ' + b.weighed_by : ''
    const src = b.weighed_source ? ' in ' + b.weighed_source : ''
    return { label: 'Weighed', value: one(b.weighed_kg) + ' kg', sub: one(b.credited_kg) + ' kg credited · weighed ' + localLabel(b.weighed_local) + by + src }
  }
  const sub = b.gross_kg !== null && Math.abs(b.gross_kg - b.credited_kg) > 0.05 ? one(b.gross_kg) + ' kg gross before quality' : 'credited, after quality'
  return { label: 'Weight', value: one(b.credited_kg) + ' kg', sub }
}

// V4 bxStackVals, "How this weight was worked out" (§7k.2c). The shares are
// the API's split (services/stack-weighing.js splitStack: by field estimate,
// to the hundredth, the last bin with an estimate taking the rounding), shown
// to one decimal. A bin with no estimate takes none; a bin whose weight has
// since been set in Control keeps its place in the split, but what it holds
// is Control's figure, not its share.
const r2 = (n: number) => Math.round(n * 100) / 100
export function stackShares(net: number, estimates: number[]): number[] {
  const total = r2(estimates.reduce((a, e) => a + e, 0))
  if (!(total > 0)) return estimates.map(() => 0)
  const last = estimates.map((e) => e > 0).lastIndexOf(true)
  let used = 0
  return estimates.map((e, i) => {
    if (!(e > 0)) return 0
    const kg = i === last ? r2(net - used) : r2(net * (e / total))
    used = r2(used + kg)
    return kg
  })
}
export function stackView(w: Weighing, openId: string) {
  const total = w.bins.reduce((a, b) => a + b.estimate_kg, 0)
  const shares = stackShares(w.net_kg, w.bins.map((b) => b.estimate_kg))
  const rows = w.bins.map((b, i) => ({
    id: b.batch_id,
    mine: b.batch_id === openId,
    bin: 'Bin ' + (b.bin_code || '—'),
    code: b.batch_code,
    tare: one(b.tare_kg) + ' kg',
    est: one(b.estimate_kg) + ' kg',
    pct: (total > 0 ? Math.round((b.estimate_kg / total) * 100) : 0) + '%',
    share: b.linked ? one(shares[i]) + ' kg' : 'Set in Control',
    kg: shares[i],
  }))
  const others = w.bins.length - 1
  const mine = rows.find((r) => r.mine)
  return {
    rows,
    head:
      'Weighed with ' + others + ' other bin' + (others === 1 ? '' : 's') + ' · ' + localLabel(w.captured_local) + ' by ' + w.operator + ' in ' + w.source,
    sum:
      'Scale read ' + one(w.gross_kg) + ' kg, less ' + one(w.tare_total_kg) + ' kg of empty bin weight, leaves ' + one(w.net_kg) +
      ' kg net. That net is shared across the bins in proportion to their field estimates.' +
      (w.tare_source === 'device' ? ' The empty weight is the one the device recorded with the weighing; CONTROL does not edit it.' : ''),
    mine: !mine
      ? ''
      : mine.kg > 0
        ? 'This bin held ' + mine.pct + ' of the field estimate, so it took ' + mine.pct + ' of the net: ' + mine.share +
          '. Inside the bin, that weight is then shared across its cycles the same way.'
        : 'This bin had no field estimate, so it took no share of the net. Its empty weight still came off the scale reading.',
  }
}

export const qcLine = (b: Batch) =>
  b.qc > 0 ? b.qc + ' of ' + b.live + ' cycles carry a quality deduction' : 'No quality deductions recorded against these cycles'

// History, newest first as the API sorts it. "Opened" is a device event from
// the engine; a batch older than the batch_events table falls back to its
// started stamp so the list never starts in mid-air.
export function historyRows(b: Batch, history: HistoryEntry[]) {
  const rows = history.map((e) => {
    const manual = e.source === 'control'
    const crew = e.source === 'Dataphyll Crew'
    const src = manual ? 'Control' : e.source === 'system' ? 'Automatic' : crew ? 'Dataphyll Crew' : 'Device'
    return {
      id: e.id,
      t: localLabel(e.at_local),
      what: e.what,
      why: e.why || '',
      who: (e.who || '—') + ' · ' + src,
      manual,
      kind: crew ? 'Crew' : manual ? 'Manual' : e.source === 'system' ? 'Automatic' : 'Device',
    }
  })
  if (!history.some((e) => e.type === 'opened') && b.started_local) {
    rows.push({
      id: 'opened',
      t: localLabel(b.started_local),
      what: 'Batch opened. First bucket scanned in',
      why: '',
      who: (b.started_by || 'Field station') + ' · Device',
      manual: false,
      kind: 'Device',
    })
  }
  return rows
}

export type ActKey = 'complete' | 'receipt' | 'weight' | 'bin' | 'job' | 'temperatures' | 'rename' | 'exclude'

// V4 ACTS: the correction footer's title, field label and note.
export const ACTS: Record<ActKey, { title: string; label: string; note: string }> = {
  rename: {
    title: 'Rename this batch',
    label: 'New batch ID',
    note: 'The new ID must be unique in this subscription. Everything that points at the old ID moves with it.',
  },
  weight: {
    title: 'Override the bin weight',
    label: 'Weighed bin weight (kg)',
    note: 'The scale at the packhouse is the truth; the cycle weights inside are estimates from the field. Setting the bin weight shares it across the cycles in proportion to what each already holds. A 100 kg bin of 30/20/20/20 set to 50 kg becomes 15/10/10/10. And records an adjustment on every one of them. Available on a completed bin only, and any cycle that arrives afterwards re-shares the same total.',
  },
  bin: { title: 'Correct the bin ID', label: 'Bin ID', note: 'Use when the wrong physical bin was scanned. It does not move any cycles.' },
  job: {
    title: 'Change the picking job',
    label: 'Picking job',
    note: 'Applies to every cycle in the batch and is recorded as an adjustment on each of them.',
  },
  temperatures: { title: 'Edit temperatures', label: 'Pick temperature (°C)', note: 'Recorded against the batch, not the individual cycles.' },
  complete: {
    title: 'Mark completed in field',
    label: '',
    note: 'For a batch the field station never closed. It is recorded as a manual completion, not a device event.',
  },
  receipt: {
    title: 'Mark receipted at packhouse',
    label: '',
    note: 'Only for a bin that physically arrived but was never scanned. Receipting is otherwise an explicit scan of the bin QR.',
  },
  exclude: {
    title: 'Exclude this batch',
    label: '',
    note: 'Excludes every cycle in the batch from production. Each cycle keeps its record and can be included again individually.',
  },
}

// The reason each correction records when none is typed (the API's
// DEFAULT_REASON, shown prefilled so the person sees what will be written).
export const DEFAULT_REASON: Record<ActKey, string> = {
  complete: 'Field station never recorded End Batch',
  receipt: 'Bin arrived but the QR was never scanned',
  bin: 'Wrong bin scanned',
  job: 'Batch scanned against the wrong job',
  temperatures: 'Manual correction',
  rename: 'Manual correction',
  exclude: 'Whole batch excluded',
  weight: 'Weighed at the packhouse',
}

// V4 bxCorrections, in order, with what hides each.
export function corrections(b: Batch): { key: ActKey; label: string }[] {
  const all: { key: ActKey; label: string; hide?: boolean }[] = [
    { key: 'complete', label: 'Mark complete', hide: !!b.completed_at },
    { key: 'receipt', label: 'Mark receipted', hide: !!b.receipted_at },
    { key: 'weight', label: 'Override bin weight', hide: !b.completed_at },
    { key: 'bin', label: 'Correct bin ID' },
    { key: 'job', label: 'Change picking job' },
    { key: 'temperatures', label: 'Edit temperatures' },
    { key: 'rename', label: 'Rename batch' },
  ]
  return all.filter((a) => !a.hide)
}

// The value a correction's field starts at (V4 bxActVal).
export function initialValues(b: Batch, act: ActKey): [string, string] {
  switch (act) {
    case 'weight':
      return [String(b.weighed_kg ?? b.gross_kg ?? ''), '']
    case 'bin':
      return [b.bin_code || '', '']
    case 'rename':
      return [b.batch_code, '']
    case 'job':
      return [b.picking_job_id || '', '']
    case 'temperatures':
      return [b.pick_temp_c === null ? '' : String(b.pick_temp_c), b.packhouse_temp_c === null ? '' : String(b.packhouse_temp_c)]
    default:
      return ['', '']
  }
}

// The request body for a correction, or the refusal a person can fix before
// sending (the API refuses the same things; this only saves a round trip).
export function actBody(b: Batch, act: ActKey, v1: string, v2: string, reason: string): { body: Record<string, unknown> } | { error: string } {
  const r = { reason: reason.trim() || DEFAULT_REASON[act] }
  switch (act) {
    case 'rename':
      return v1.trim() ? { body: { ...r, batch_code: v1.trim() } } : { error: 'Enter a batch ID' }
    case 'bin':
      return v1.trim() ? { body: { ...r, bin_code: v1.trim() } } : { error: 'Enter a bin ID' }
    case 'weight': {
      const n = Number(v1)
      return Number.isFinite(n) && n > 0 ? { body: { ...r, weighed_kg: n } } : { error: 'Enter the weighed bin weight in kilograms' }
    }
    case 'job':
      return v1 && v1 !== b.picking_job_id ? { body: { ...r, picking_job_id: v1 } } : { error: 'Pick a different picking job' }
    case 'temperatures':
      return { body: { ...r, pick_temp_c: v1.trim(), packhouse_temp_c: v2.trim() } }
    default:
      return { body: r }
  }
}

// V4 bxCogCount: how many loaded batches the typed limit would flag.
export function overCountLine(rows: Batch[], typed: string): string {
  const m = parseFloat(typed)
  const n = Number.isFinite(m) && m > 0 ? rows.filter((b) => !b.excluded && b.in_bin_kg > m).length : 0
  return n === 0 ? 'No batch in this period is over that weight' : n + (n === 1 ? ' batch is' : ' batches are') + ' over that weight right now'
}

// The export (§7k.7): a sheet of the batches in view and a sheet of their
// member cycles, numbers as numbers.
export function batchSheet(rows: Batch[]): CellValue[][] {
  const head = [
    'Batch', 'Status', 'Bin', 'Product', 'Picking job', 'Grower / orchard', 'Rows picked from', 'Items', 'In bin kg', 'Credited kg',
    'Weighed kg', 'First day', 'Last day', 'Last activity', 'Completed in field', 'Receipted', 'Pick temp °C', 'Packhouse temp °C', 'Warnings',
  ]
  return [
    head,
    ...rows.map((b) => [
      b.batch_code,
      statusOf(b).label,
      b.bin_code,
      b.product,
      b.picking_job_name,
      b.site_name,
      b.blocks.join(', '),
      b.items,
      b.in_bin_kg,
      b.credited_kg,
      b.weighed_kg,
      b.dates[0] || b.work_date,
      b.dates[b.dates.length - 1] || b.work_date,
      b.last_local,
      b.completed_local,
      b.receipted_local,
      b.pick_temp_c,
      b.packhouse_temp_c,
      warningChips(b).map((c) => c.label).join('; '),
    ]),
  ]
}

export function cycleSheet(cycles: Cycle[], codes: Record<string, string>): CellValue[][] {
  const head = ['Batch', 'Date', 'Time', 'Item', 'Worker', 'Site', 'Location', 'Picking job', 'Product', 'Gross kg', 'QC factor', 'Credited kg', 'Quality', 'Status']
  return [
    head,
    ...cycles.map((c) => [
      (c.batch_id && codes[c.batch_id]) || c.batch_code,
      c.work_date,
      c.eff_time,
      itemExport(c),
      c.worker_name || 'Unassigned',
      c.site_name,
      c.location,
      c.picking_job_name,
      c.product,
      c.gross_kg,
      c.qc_factor,
      c.credited_kg,
      c.quality_name,
      c.status,
    ]),
  ]
}
